import {
  Box,
  Button,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { ITask } from '../../../../models/task.model';
import { AppState } from '../../../../state/configureStore';
import {
  clearTaskItem,
  deleteTaskItem,
  fetchTaskItemListByTaskId,
  TaskItemState,
} from '../../../../state/taskItem';
import TaskItemCreateDialog from './TaskItemCreateDialog';
import useStyles from './taskItemList.style';
import { useTableSorting } from '../../../hooks';
import { OpportunityLinkButton, SortLabel } from '../../../components';
import SalesTeamMemberDialog from '../../../components/SalesTeamComboBox/SalesTeamMemberDialog';

interface TaskItemListProps {
  smTaskId: ITask['id'];
}

const TaskItemList: React.FC<TaskItemListProps> = (props) => {
  const { smTaskId } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openTaskItemCreateDialog, setOpenTaskItemCreateDialog] = useState(false);
  const [openSalesTeamMemberDialog, setOpenSalesTeamMemberDialog] = useState(false);
  const [selectedSalesTeamId, setSelectedSalesTeamId] = useState(0);
  const [sort, setSort] = useTableSorting({ orderBy: 'id', direction: 'asc' }, true);
  const { items: itemList, createdItems } = useSelector<AppState, TaskItemState>(
    (state) => state.taskItem,
  );

  useEffect(() => {
    dispatch(fetchTaskItemListByTaskId(smTaskId!, sort));
  }, [dispatch, smTaskId, createdItems, sort]);

  useEffect(() => {
    return function cleanUp() {
      dispatch(clearTaskItem());
    };
  }, [dispatch]);

  const deleteSMTaskItem = async (id?: number) => {
    if (!id) {
      return;
    }

    const result = await Swal.fire({
      title: 'Confirm Delete',
      text: 'Are you sure you want to delete this task item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    });

    if (result.isConfirmed) {
      dispatch(deleteTaskItem({ id, smTaskId }));
    }
  };

  return (
    <>
      <Paper elevation={2} style={{ padding: 12, borderRadius: 16 }}>
        <CardContent style={{ padding: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenTaskItemCreateDialog(true);
            }}
            className={classes.createTaskItemButton}
          >
            <AddIcon className={classes.leftIcon} />
            New Task Item
          </Button>
          <TableContainer>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>No.</TableCell>
                  <TableCell>
                    <SortLabel
                      sort={sort}
                      handleSortChange={setSort}
                      field="OpportunityItem.Opportunity.AssignmentNumber"
                    >
                      Assignment Number
                    </SortLabel>
                  </TableCell>
                  <TableCell>Program</TableCell>
                  {/* <TableCell>Brand</TableCell> */}
                  <TableCell>
                    <SortLabel
                      sort={sort}
                      handleSortChange={setSort}
                      field="OpportunityItem.Block.BlockNo"
                    >
                      Block
                    </SortLabel>
                  </TableCell>
                  <TableCell style={{ minWidth: '200px' }}>Item</TableCell>
                  <TableCell style={{ minWidth: '200px' }}>Item Description</TableCell>
                  <TableCell style={{ minWidth: '150px' }}>Sales</TableCell>
                  <TableCell style={{ minWidth: '150px' }}>SSO</TableCell>
                  <TableCell style={{ minWidth: '150px' }}>CS</TableCell>
                  <TableCell style={{ minWidth: '150px' }}>NPD</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {itemList.map((item, index) => {
                  const block = item.opportunityItem!.block!;

                  const opportunity = item.opportunityItem!.opportunity!;
                  const ssoNames =
                    opportunity.responsibility?.responsibilityWorkingTeamMembers
                      ?.filter(
                        (f) =>
                          f.active === true &&
                          f.smWorkingTeamMember?.smWorkingTeam?.smWorkingGroup?.name ===
                            'SSO',
                      )
                      ?.map((rs) => {
                        const status =
                          rs.status === 'P' ? 'SSO' : `SSO${rs.status?.replace('S', '')}`;
                        return {
                          name: rs.smWorkingTeamMember?.user?.activated
                            ? `${rs.smWorkingTeamMember?.user?.firstName}`
                            : `${rs.smWorkingTeamMember?.user?.firstName} (Inactive)`,
                          activated: rs.smWorkingTeamMember?.user?.activated,
                          status: status === 'SSO' ? 'PRIMARY' : 'SECONDARY',
                          userLeaveTotalDay:
                            rs.smWorkingTeamMember?.user?.userLeaveTotalDay,
                        };
                      })
                      ?.sort((left, right) => {
                        const activatedOrder =
                          Number(right.activated === true) -
                          Number(left.activated === true);

                        if (activatedOrder !== 0) {
                          return activatedOrder;
                        }

                        const leftStatusRank = left.status === 'PRIMARY' ? 0 : 1;
                        const rightStatusRank = right.status === 'PRIMARY' ? 0 : 1;

                        if (leftStatusRank !== rightStatusRank) {
                          return leftStatusRank - rightStatusRank;
                        }

                        return (left.name || '').localeCompare(right.name || '');
                      }) || [];

                  const csNames =
                    opportunity.responsibility?.responsibilityWorkingTeamMembers
                      ?.filter(
                        (f) =>
                          f.active === true &&
                          f.smWorkingTeamMember?.smWorkingTeam?.smWorkingGroup?.name ===
                            'CS',
                      )
                      ?.map((rs) => {
                        const status = rs.status === 'P' ? 'CS' : `C${rs.status}`;
                        return {
                          name: rs.smWorkingTeamMember?.user?.activated
                            ? `${rs.smWorkingTeamMember?.user?.firstName}`
                            : `${rs.smWorkingTeamMember?.user?.firstName} (Inactive)`,
                          activated: rs.smWorkingTeamMember?.user?.activated,
                          status: status === 'CS' ? 'PRIMARY' : 'SECONDARY',
                          userLeaveTotalDay:
                            rs.smWorkingTeamMember?.user?.userLeaveTotalDay,
                        };
                      })
                      ?.sort((left, right) => {
                        const activatedOrder =
                          Number(right.activated === true) -
                          Number(left.activated === true);

                        if (activatedOrder !== 0) {
                          return activatedOrder;
                        }

                        const leftStatusRank = left.status === 'PRIMARY' ? 0 : 1;
                        const rightStatusRank = right.status === 'PRIMARY' ? 0 : 1;

                        if (leftStatusRank !== rightStatusRank) {
                          return leftStatusRank - rightStatusRank;
                        }

                        return (left.name || '').localeCompare(right.name || '');
                      }) || [];

                  const prodType = block.productType?.name ?? '';
                  const npdNames =
                    opportunity.responsibility?.responsibilityWorkingTeamMembers
                      ?.filter(
                        (f) =>
                          f.active === true &&
                          f.smWorkingTeamMember?.smWorkingTeam?.description === prodType,
                      )
                      ?.map((rs) => {
                        return {
                          name: rs.smWorkingTeamMember?.user?.activated
                            ? `${rs.smWorkingTeamMember?.user?.firstName}`
                            : `${rs.smWorkingTeamMember?.user?.firstName} (Inactive)`,
                          activated: rs.smWorkingTeamMember?.user?.activated,
                          status: 'PRIMARY',
                          userLeaveTotalDay:
                            rs.smWorkingTeamMember?.user?.userLeaveTotalDay,
                        };
                      })
                      ?.sort((left, right) => {
                        const activatedOrder =
                          Number(right.activated === true) -
                          Number(left.activated === true);

                        if (activatedOrder !== 0) {
                          return activatedOrder;
                        }

                        const leftStatusRank = left.status === 'PRIMARY' ? 0 : 1;
                        const rightStatusRank = right.status === 'PRIMARY' ? 0 : 1;

                        if (leftStatusRank !== rightStatusRank) {
                          return leftStatusRank - rightStatusRank;
                        }

                        return (left.name || '').localeCompare(right.name || '');
                      }) || [];

                  // const salesName =
                  //   opportunity.responsibility?.responsibilitySalesTeamMembers
                  //     ?.filter((f) => f.active === true)
                  //     .map((s) => s.salesTeamMember?.salesTeam?.owner?.fullName || '') || [];
                  const salesNames =
                    opportunity.responsibility?.responsibilitySalesTeamMembers
                      ?.filter((rs) => rs.active === true)
                      ?.map((rs) => {
                        const status =
                          rs.status === 'P'
                            ? 'Sales'
                            : `Sales${rs.status?.replace('S', '')}`;
                        return {
                          name: rs.salesTeamMember?.user?.activated
                            ? `${rs.salesTeamMember?.user?.firstName}`
                            : `${rs.salesTeamMember?.user?.firstName} (Inactive)`,
                          activated: rs.salesTeamMember?.user?.activated,
                          status: status === 'Sales' ? 'PRIMARY' : 'SECONDARY',
                          userLeaveTotalDay: rs.salesTeamMember?.user?.userLeaveTotalDay,
                        };
                      })
                      ?.sort((left, right) => {
                        const activatedOrder =
                          Number(right.activated === true) -
                          Number(left.activated === true);

                        if (activatedOrder !== 0) {
                          return activatedOrder;
                        }

                        const leftStatusRank = left.status === 'PRIMARY' ? 0 : 1;
                        const rightStatusRank = right.status === 'PRIMARY' ? 0 : 1;

                        if (leftStatusRank !== rightStatusRank) {
                          return leftStatusRank - rightStatusRank;
                        }

                        return (left.name || '').localeCompare(right.name || '');
                      }) || [];

                  let npdFullName = '';
                  if (item.opportunityItem?.npdUser) {
                    const userActive = item.opportunityItem.npdUser.activated;
                    npdFullName = `${item.opportunityItem?.npdUser?.firstName} ${
                      userActive ? '' : '(Inactive)'
                    }`;
                  } else if (item.opportunityItem?.sampleDevUser) {
                    const userActive = item.opportunityItem.sampleDevUser.activated;
                    npdFullName = `${item.opportunityItem.sampleDevUser.firstName} ${
                      userActive ? '' : '(Inactive)'
                    }`;
                  }

                  let npdActivated = true;
                  if (item.opportunityItem?.npdUser) {
                    npdActivated = item.opportunityItem.npdUser.activated ?? true;
                  } else if (item.opportunityItem?.sampleDevUser) {
                    npdActivated = item.opportunityItem.sampleDevUser.activated ?? true;
                  }
                  const npdUser = {
                    name: npdFullName,
                    activated: npdActivated,
                  };

                  return (
                    <TableRow key={item.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <OpportunityLinkButton opportunity={opportunity} />
                      </TableCell>
                      <TableCell>{block.program?.name}</TableCell>
                      <TableCell>{block.blockNo}</TableCell>
                      <TableCell>{block.item}</TableCell>
                      <TableCell>{block.itemDescription}</TableCell>
                      <TableCell>
                        <Box
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px',
                            minHeight: 32,
                          }}
                        >
                          {salesNames.length > 0 ? (
                            salesNames.map((member) => {
                              const isPrimary = member.status === 'PRIMARY';
                              const isActive = member.activated !== false;

                              return (
                                <Box
                                  key={`${member.name}-${member.status}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '5px 10px',
                                    borderRadius: 10,
                                    border: isPrimary
                                      ? '1px solid rgba(37, 99, 235, 0.18)'
                                      : '1px solid rgba(148, 163, 184, 0.22)',
                                    background: isPrimary
                                      ? 'linear-gradient(135deg, rgba(239, 246, 255, 0.96) 0%, rgba(224, 231, 255, 0.92) 100%)'
                                      : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(241, 245, 249, 0.95) 100%)',
                                    color: isActive ? '#0f172a' : '#94a3b8',
                                    boxShadow: isPrimary
                                      ? '0 6px 16px rgba(37, 99, 235, 0.10)'
                                      : '0 4px 12px rgba(148, 163, 184, 0.10)',
                                    opacity: isActive ? 1 : 0.75,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: 999,
                                      backgroundColor: isPrimary ? '#2563eb' : '#94a3b8',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: '0.78rem',
                                      fontWeight: 700,
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {member.name}
                                  </span>
                                </Box>
                              );
                            })
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                              -
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px',
                            minHeight: 32,
                          }}
                        >
                          {ssoNames.length > 0 ? (
                            ssoNames.map((member) => {
                              const isPrimary = member.status === 'PRIMARY';
                              const isActive = member.activated !== false;

                              return (
                                <Box
                                  key={`${member.name}-${member.status}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '5px 10px',
                                    borderRadius: 10,
                                    border: isPrimary
                                      ? '1px solid rgba(37, 99, 235, 0.18)'
                                      : '1px solid rgba(148, 163, 184, 0.22)',
                                    background: isPrimary
                                      ? 'linear-gradient(135deg, rgba(239, 246, 255, 0.96) 0%, rgba(224, 231, 255, 0.92) 100%)'
                                      : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(241, 245, 249, 0.95) 100%)',
                                    color: isActive ? '#0f172a' : '#94a3b8',
                                    boxShadow: isPrimary
                                      ? '0 6px 16px rgba(37, 99, 235, 0.10)'
                                      : '0 4px 12px rgba(148, 163, 184, 0.10)',
                                    opacity: isActive ? 1 : 0.75,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: 999,
                                      backgroundColor: isPrimary ? '#2563eb' : '#94a3b8',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: '0.78rem',
                                      fontWeight: 700,
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {member.name}
                                  </span>
                                </Box>
                              );
                            })
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                              -
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px',
                            minHeight: 32,
                          }}
                        >
                          {csNames.length > 0 ? (
                            csNames.map((member) => {
                              const isPrimary = member.status === 'PRIMARY';
                              const isActive = member.activated !== false;

                              return (
                                <Box
                                  key={`${member.name}-${member.status}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '5px 10px',
                                    borderRadius: 10,
                                    border: isPrimary
                                      ? '1px solid rgba(37, 99, 235, 0.18)'
                                      : '1px solid rgba(148, 163, 184, 0.22)',
                                    background: isPrimary
                                      ? 'linear-gradient(135deg, rgba(239, 246, 255, 0.96) 0%, rgba(224, 231, 255, 0.92) 100%)'
                                      : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(241, 245, 249, 0.95) 100%)',
                                    color: isActive ? '#0f172a' : '#94a3b8',
                                    boxShadow: isPrimary
                                      ? '0 6px 16px rgba(37, 99, 235, 0.10)'
                                      : '0 4px 12px rgba(148, 163, 184, 0.10)',
                                    opacity: isActive ? 1 : 0.75,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: 999,
                                      backgroundColor: isPrimary ? '#2563eb' : '#94a3b8',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: '0.78rem',
                                      fontWeight: 700,
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {member.name}
                                  </span>
                                </Box>
                              );
                            })
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                              -
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px',
                            minHeight: 32,
                          }}
                        >
                          {npdNames.length > 0 ? (
                            npdNames.map((member) => {
                              const isPrimary = member.status === 'PRIMARY';
                              const isActive = member.activated !== false;

                              return (
                                <Box
                                  key={`${member.name}-${member.status}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '5px 10px',
                                    borderRadius: 10,
                                    border: isPrimary
                                      ? '1px solid rgba(37, 99, 235, 0.18)'
                                      : '1px solid rgba(148, 163, 184, 0.22)',
                                    background: isPrimary
                                      ? 'linear-gradient(135deg, rgba(239, 246, 255, 0.96) 0%, rgba(224, 231, 255, 0.92) 100%)'
                                      : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(241, 245, 249, 0.95) 100%)',
                                    color: isActive ? '#0f172a' : '#94a3b8',
                                    boxShadow: isPrimary
                                      ? '0 6px 16px rgba(37, 99, 235, 0.10)'
                                      : '0 4px 12px rgba(148, 163, 184, 0.10)',
                                    opacity: isActive ? 1 : 0.75,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: 999,
                                      backgroundColor: isPrimary ? '#2563eb' : '#94a3b8',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: '0.78rem',
                                      fontWeight: 700,
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {member.name}
                                  </span>
                                </Box>
                              );
                            })
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                              -
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="default"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => deleteSMTaskItem(item.id)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Paper>
      {openTaskItemCreateDialog && (
        <TaskItemCreateDialog
          open={openTaskItemCreateDialog}
          handleClose={() => {
            setOpenTaskItemCreateDialog(false);
          }}
        />
      )}
      <SalesTeamMemberDialog
        open={openSalesTeamMemberDialog}
        handleClose={() => setOpenSalesTeamMemberDialog(false)}
        salesTeamId={selectedSalesTeamId}
      />
    </>
  );
};

export default TaskItemList;
